import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  getUserDetails, AddUsers, AddEvt_Organiser,
  AddEvt_UserOrganiserMapping, getOrganisersByUser, sendOtp, verifyOtp,
} from '../../api/api';
import { tenDigits } from '../../utils/helpers';
import { passwordProblem, verifyDevice, thisDeviceId } from '../../utils/authFlow';
import { startSessionLog } from '../../utils/session';

// Self-onboarding, in two steps:
//   details → the number must be free, then a WhatsApp OTP proves it is really
//             this person's phone (nobody can sign up with someone else's number)
//   otp     → user → organiser (owned by that user) → owner mapping → the account
//             is BOUND to this device (verifyDevice with bind) and signed in, so
//             the next launch needs no login at all.
// Meta bills each OTP, so the API re-uses a code sent within the last hour.
export function useRegister() {
  const { login } = useAuth();
  const nav = useNavigate();
  const { state } = useLocation();
  const [form, setForm] = useState({
    name: '', surname: '', mobile: String(state?.mobile || '').replace(/\D/g, '').slice(0, 10),
    password: '', confirm: '', organiser: '',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [step, setStep] = useState('details');
  const [otp, setOtpRaw] = useState('');
  const [reused, setReused] = useState(null);
  // The typed mobile is registered already -> offer Sign in / reset with OTP
  // instead of a second account.
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);

  const set = (k, v) => {
    const val = k === 'mobile' ? tenDigits(v) : v; // last 10 digits (autofill gives +91 …)
    setForm((f) => ({ ...f, [k]: val }));
    if (k === 'mobile') setAlreadyRegistered(false);
  };

  const setOtp = (v) => setOtpRaw(String(v || '').replace(/\D/g, '').slice(0, 4));

  const requestOtp = async () => {
    const r = await sendOtp({ MobileNumber: form.mobile, ServiceBy: 'Mehfil by Smartify' });
    setReused(r?.reused ? r.Date_Time : null);
  };

  // Step 1: check the form, make sure the number is free, send the OTP.
  const submit = async (e) => {
    e.preventDefault();
    setErr(''); setAlreadyRegistered(false);
    const { name, mobile, password, confirm, organiser } = form;
    if (!name.trim()) { setErr('Enter your name'); return; }
    if (!/^\d{10}$/.test(mobile)) { setErr('Enter a valid 10-digit mobile number'); return; }
    if (!password) { setErr('Enter a password'); return; }
    { const problem = passwordProblem(password, mobile); if (problem) { setErr(problem); return; } }
    if (password !== confirm) { setErr('Passwords do not match'); return; }
    if (!organiser.trim()) { setErr('Enter your organiser / business name'); return; }

    setBusy(true);
    try {
      const existing = await getUserDetails({ MobileNumber: mobile });
      if (Array.isArray(existing) && existing.length > 0) {
        setErr('This mobile number is already registered.');
        setAlreadyRegistered(true);
        return;
      }
      await requestOtp();
      setOtpRaw('');
      setStep('otp');
    } catch {
      setErr('Could not send the OTP. Check your connection and try again.');
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setErr(''); setBusy(true);
    try { await requestOtp(); } catch { setErr('Could not resend the OTP'); } finally { setBusy(false); }
  };

  // Step 2: the code proves the phone -> create the account and sign in here.
  const submitOtp = async (e) => {
    e.preventDefault();
    setErr('');
    const { name, surname, mobile, password, organiser } = form;
    if (!/^\d{4}$/.test(otp)) { setErr('Enter the 4-digit OTP'); return; }

    setBusy(true);
    try {
      const res = await verifyOtp({ MobileNumber: mobile, OTP: otp });
      if (!res?.verified) { setErr(res?.reason || 'Invalid OTP'); return; }
      // 2) Create the user.
      const userRes = await AddUsers({
        MobileNumber: mobile, Password: password, Name: name, Surname: surname, Valid: 1,
      });
      const userId = userRes && userRes.insertId;
      if (!userId) { setErr('Could not create the user account.'); return; }
      // 3) Create the organiser (tenant), owned by this user.
      const orgRes = await AddEvt_Organiser({
        OrganiserName: organiser, OrganiserMasterUser: userId,
        MobileNumber: mobile, Valid: 1, CreatedBy: userId,
      });
      const organiserId = orgRes && orgRes.insertId;
      if (!organiserId) { setErr('Account created, but the organiser could not be set up.'); return; }
      // 4) Link user ↔ organiser as its Owner (IsAdmin=1 → full access; login requires this mapping).
      await AddEvt_UserOrganiserMapping({
        UserId: userId, OrganiserId: organiserId, IsAdmin: 1, Valid: 1, CreatedBy: userId,
      });
      // 5) Bind THIS device to the new account and sign in. Binding writes the
      //    device id into Evt_Users, and the credentials are kept, so the next
      //    launch signs in by itself - no password, no second OTP.
      const bound = await verifyDevice(mobile, password, { bind: true });
      const users = await getUserDetails({ MobileNumber: mobile });
      const user = bound?.user || (Array.isArray(users) ? users[0] : null);
      const organisers = await getOrganisersByUser({ UserId: userId });
      const list = Array.isArray(organisers) ? organisers : [];
      if (user && list.length > 0) {
        login(user, list[0], { mobile, password }, true);
        startSessionLog(user, await thisDeviceId());
        nav('/app/dashboard');
      } else {
        nav('/', { state: { registered: true } });
      }
    } catch {
      setErr('Registration failed. Check your connection and try again.');
    } finally {
      setBusy(false);
    }
  };

  const forgotPassword = () => nav('/forgot-password', { state: { mobile: form.mobile } });
  const back = () => { setErr(''); setStep('details'); };

  return {
    form, set, busy, err, alreadyRegistered, forgotPassword, submit,
    step, otp, setOtp, submitOtp, resend, reused, back,
  };
}
